import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import UnfoldLessOutlinedIcon from '@mui/icons-material/UnfoldLessOutlined';
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import React, { useMemo, useState } from 'react';
import {
  buildVizSimTags,
  getVizSimTagsByPrefix,
  getVizSimTagValue,
  normalizeTagPart,
  normalizeVizSimTags,
} from '../../utils/vizsimTags';

const TYPE_OPTIONS = [
  { value: 'product', label: 'product' },
  { value: 'carton', label: 'carton' },
  { value: 'mixed', label: 'mixed' },
];
const ORIENTATION_OPTIONS = [
  { value: 'front', label: 'front' },
  { value: 'nonfront', label: 'non-front' },
  { value: 'mixed', label: 'mixed' },
];
const N_STORES_OPTIONS = [
  { value: 'one', label: 'one' },
  { value: 'multiple', label: 'multiple' },
];

interface VizSimTagEditorProps {
  tags: string[];
  existingTags?: string[];
  globalTags?: string[];
  modPrefixes?: string[];
  onChange: (tags: string[]) => void;
}

function orderedUnique(values: string[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  values.forEach((value) => {
    const normalized = value.trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    ordered.push(normalized);
  });
  return ordered;
}

function tagValues(tags: string[]) {
  const normalizedTags = normalizeVizSimTags(tags);
  return {
    type: getVizSimTagValue(normalizedTags, 'type:') ?? 'mixed',
    orientation: getVizSimTagValue(normalizedTags, 'orientation:') ?? 'mixed',
    nStores: getVizSimTagValue(normalizedTags, 'n_stores:') ?? 'multiple',
    creator: getVizSimTagValue(normalizedTags, 'creator:'),
    mods: getVizSimTagsByPrefix(normalizedTags, 'mod:'),
    subsets: getVizSimTagsByPrefix(normalizedTags, 'subset:'),
  };
}

export const VizSimTagEditor: React.FC<VizSimTagEditorProps> = ({
  tags,
  existingTags = [],
  globalTags = [],
  modPrefixes = [],
  onChange,
}) => {
  const [modInput, setModInput] = useState('');
  const [subsetInput, setSubsetInput] = useState('');
  const [showGlobalMods, setShowGlobalMods] = useState(false);
  const [showGlobalSubsets, setShowGlobalSubsets] = useState(false);
  const values = useMemo(() => tagValues(tags), [tags]);
  const existingValues = useMemo(() => tagValues(existingTags), [existingTags]);
  const globalValues = useMemo(() => tagValues(globalTags), [globalTags]);
  const modOptions = useMemo(
    () => orderedUnique([...values.mods, ...modPrefixes, ...existingValues.mods]).sort(),
    [existingValues.mods, modPrefixes, values.mods]
  );
  const subsetOptions = useMemo(
    () => orderedUnique([...values.subsets, ...existingValues.subsets]).sort(),
    [existingValues.subsets, values.subsets]
  );
  // Tags used in other chains but not in this one stay hidden until requested.
  const globalOnlyModOptions = useMemo(
    () => globalValues.mods.filter((mod) => !modOptions.includes(mod)).sort(),
    [globalValues.mods, modOptions]
  );
  const globalOnlySubsetOptions = useMemo(
    () => globalValues.subsets.filter((subset) => !subsetOptions.includes(subset)).sort(),
    [globalValues.subsets, subsetOptions]
  );
  const visibleModOptions = showGlobalMods ? [...modOptions, ...globalOnlyModOptions] : modOptions;
  const visibleSubsetOptions = showGlobalSubsets
    ? [...subsetOptions, ...globalOnlySubsetOptions]
    : subsetOptions;

  const emit = (next: Partial<ReturnType<typeof tagValues>>) => {
    onChange(
      buildVizSimTags({
        type: next.type ?? values.type,
        orientation: next.orientation ?? values.orientation,
        nStores: next.nStores ?? values.nStores,
        creator: values.creator,
        mods: next.mods ?? values.mods,
        subsets: next.subsets ?? values.subsets,
      })
    );
  };

  const toggleValue = (kind: 'mods' | 'subsets', value: string) => {
    const currentValues = values[kind];
    if (kind === 'mods') {
      emit({ mods: currentValues.includes(value) ? [] : [value] });
      return;
    }
    emit({
      subsets: currentValues.includes(value)
        ? currentValues.filter((current) => current !== value)
        : [...currentValues, value],
    });
  };

  const addValue = (kind: 'mods' | 'subsets') => {
    const rawValue = kind === 'mods' ? modInput : subsetInput;
    const normalized = kind === 'mods' ? rawValue.trim().toUpperCase() : normalizeTagPart(rawValue);
    if (!normalized) return;
    emit(
      kind === 'mods'
        ? { mods: [normalized] }
        : { subsets: orderedUnique([...values.subsets, normalized]) }
    );
    if (kind === 'mods') setModInput('');
    else setSubsetInput('');
  };

  const handleInputKeyDown =
    (kind: 'mods' | 'subsets') => (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        event.key !== 'Enter' ||
        event.shiftKey ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      ) {
        return;
      }
      event.preventDefault();
      addValue(kind);
    };

  return (
    <div className="vizsim-tag-editor">
      <span className="vizsim-tag-editor-title">Tags</span>
      <div className="vizsim-tag-editor-row">
        <span>type</span>
        <div>
          {TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`vizsim-tag-option ${values.type === option.value ? 'is-active' : ''}`}
              aria-pressed={values.type === option.value}
              onClick={() => emit({ type: option.value })}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <div className="vizsim-tag-editor-row">
        <span>orientation</span>
        <div>
          {ORIENTATION_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`vizsim-tag-option ${values.orientation === option.value ? 'is-active' : ''}`}
              aria-pressed={values.orientation === option.value}
              onClick={() => emit({ orientation: option.value })}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <div className="vizsim-tag-editor-row">
        <span>n_stores</span>
        <div>
          {N_STORES_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`vizsim-tag-option ${values.nStores === option.value ? 'is-active' : ''}`}
              aria-pressed={values.nStores === option.value}
              onClick={() => emit({ nStores: option.value })}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <div className="vizsim-tag-editor-row">
        <span>mod</span>
        <div>
          {visibleModOptions.map((mod) => (
            <button
              key={mod}
              type="button"
              className={`vizsim-tag-option ${values.mods.includes(mod) ? 'is-active' : ''}`}
              aria-pressed={values.mods.includes(mod)}
              onClick={() => toggleValue('mods', mod)}
            >
              {mod}
            </button>
          ))}
          {globalOnlyModOptions.length > 0 ? (
            <button
              type="button"
              className="vizsim-tag-show-more"
              aria-expanded={showGlobalMods}
              aria-label={
                showGlobalMods
                  ? 'Show fewer mod tags'
                  : `Show ${globalOnlyModOptions.length} more mod tag${
                      globalOnlyModOptions.length === 1 ? '' : 's'
                    } from other chains`
              }
              title={
                showGlobalMods
                  ? 'Show fewer mod tags'
                  : `Show ${globalOnlyModOptions.length} more mod tag${
                      globalOnlyModOptions.length === 1 ? '' : 's'
                    } from other chains`
              }
              onClick={() => setShowGlobalMods((current) => !current)}
            >
              {showGlobalMods ? <UnfoldLessOutlinedIcon /> : <UnfoldMoreOutlinedIcon />}
              {showGlobalMods ? null : <span>+{globalOnlyModOptions.length}</span>}
            </button>
          ) : null}
          <input
            className="vizsim-tag-editor-input"
            value={modInput}
            aria-label="New mod tag"
            placeholder="Add mod"
            onChange={(event) => setModInput(event.target.value)}
            onKeyDown={handleInputKeyDown('mods')}
          />
          <button
            type="button"
            className="vizsim-tag-add-button"
            aria-label="Add mod tag"
            disabled={!modInput.trim()}
            onClick={() => addValue('mods')}
          >
            <AddOutlinedIcon />
          </button>
        </div>
      </div>
      <div className="vizsim-tag-editor-row">
        <span>subset</span>
        <div>
          {visibleSubsetOptions.map((subset) => (
            <button
              key={subset}
              type="button"
              className={`vizsim-tag-option ${values.subsets.includes(subset) ? 'is-active' : ''}`}
              aria-pressed={values.subsets.includes(subset)}
              onClick={() => toggleValue('subsets', subset)}
            >
              {subset}
            </button>
          ))}
          {globalOnlySubsetOptions.length > 0 ? (
            <button
              type="button"
              className="vizsim-tag-show-more"
              aria-expanded={showGlobalSubsets}
              aria-label={
                showGlobalSubsets
                  ? 'Show fewer subset tags'
                  : `Show ${globalOnlySubsetOptions.length} more subset tag${
                      globalOnlySubsetOptions.length === 1 ? '' : 's'
                    } from other chains`
              }
              title={
                showGlobalSubsets
                  ? 'Show fewer subset tags'
                  : `Show ${globalOnlySubsetOptions.length} more subset tag${
                      globalOnlySubsetOptions.length === 1 ? '' : 's'
                    } from other chains`
              }
              onClick={() => setShowGlobalSubsets((current) => !current)}
            >
              {showGlobalSubsets ? <UnfoldLessOutlinedIcon /> : <UnfoldMoreOutlinedIcon />}
              {showGlobalSubsets ? null : <span>+{globalOnlySubsetOptions.length}</span>}
            </button>
          ) : null}
          <input
            className="vizsim-tag-editor-input"
            value={subsetInput}
            aria-label="New subset tag"
            placeholder="Add subset"
            onChange={(event) => setSubsetInput(event.target.value)}
            onKeyDown={handleInputKeyDown('subsets')}
          />
          <button
            type="button"
            className="vizsim-tag-add-button"
            aria-label="Add subset tag"
            disabled={!subsetInput.trim()}
            onClick={() => addValue('subsets')}
          >
            <AddOutlinedIcon />
          </button>
        </div>
      </div>
      <div className="vizsim-tag-editor-row">
        <span>creator</span>
        <div>
          {values.creator ? (
            <span className="vizsim-tag-option is-active is-readonly">{values.creator}</span>
          ) : (
            <span className="system-health-muted">Assigned on save</span>
          )}
        </div>
      </div>
    </div>
  );
};
